/**
 * Everything that happens to a career between landing and the next briefing.
 *
 * One function, `applyDebrief`, takes the sortie's debriefing and returns the
 * career it produced plus a `CareerConsequence` — the screen the original calls
 * up between missions, holding the credited claims, any decoration, any
 * promotion, any Board of Inquiry, whatever the crash did to the pilot, and any
 * new aeroplane the war has just introduced.
 *
 * The order matters and is the order the game implies: claims are credited
 * first (medals key off confirmed victories), then decorations, then promotion
 * (which keys off the score the decorations just raised), then discipline, then
 * the pilot's own condition, then the calendar.
 *
 * **The between-missions interval is port-side.** Nothing decoded says how much
 * time a career spends between sorties; four to nine days is chosen so a
 * January 1918 enlistment reaches the armistice in a playable number of
 * missions while a December 1915 one is the long war it should be.
 */

import type { AircraftKey } from "../flight/aircraft-table.ts";
import type { MissionDebrief } from "../mission/debrief.ts";
import { MissionDraw } from "../mission/mission-build.ts";
import { FAMOUS_ACES } from "../mission/mission-roster.ts";
import { frontLineScouts, introducedBetween, introductionMessage } from "./aircraft-service.ts";
import { awardScore, awardsEarned, citationLines } from "./awards.ts";
import { boardHearing, type BoardHearing } from "./board-of-inquiry.ts";
import { WAR_END_DATE, addDays, isOnOrAfter, type CareerDate } from "./calendar.ts";
import { appendLog, totalVictories, type CareerState, type MedalAward } from "./career-state.ts";
import { careerEpitaph, careerOutcome, type CareerOutcome } from "./career-outcome.ts";
import { resolveCasualty, type CasualtyReport } from "./casualty.ts";
import { promotionMessage, rankForScore, SERVICE_LABELS, serviceFor } from "./service.ts";
import { addTallies, creditVictories, summariseClaims, type VictoryClaim } from "./victory-credit.ts";

/** Days between sorties. Port-side — see the module note. */
export const MISSION_INTERVAL_DAYS: readonly [number, number] = [4, 9];

export interface PromotionNotice {
  readonly rank: number;
  readonly title: string;
  /** Verbatim `PS.EXE`: `"You are promoted to %r."` */
  readonly message: string;
}

export interface AircraftIntroduction {
  readonly aircraft: AircraftKey;
  readonly message: string;
}

export interface CareerEnding {
  readonly status: CareerState["status"];
  readonly outcome: CareerOutcome;
  readonly epitaph: string;
}

export interface CareerConsequence {
  /** The day the sortie was flown. */
  readonly date: CareerDate;
  readonly missionTitle: string;
  readonly missionScore: number;
  readonly claims: readonly VictoryClaim[];
  readonly confirmed: number;
  readonly unconfirmed: number;
  readonly awards: readonly MedalAward[];
  readonly citations: readonly (readonly string[])[];
  readonly promotion: PromotionNotice | undefined;
  readonly hearing: BoardHearing | undefined;
  readonly casualty: CasualtyReport | undefined;
  readonly introductions: readonly AircraftIntroduction[];
  /** Where the career's calendar stands afterwards. */
  readonly nextDate: CareerDate;
  readonly ending: CareerEnding | undefined;
}

export interface CareerUpdate {
  readonly career: CareerState;
  readonly consequence: CareerConsequence;
}

interface Credit {
  readonly career: CareerState;
  readonly claims: readonly VictoryClaim[];
  readonly confirmed: number;
  readonly unconfirmed: number;
}

function creditClaims(career: CareerState, debrief: MissionDebrief): Credit {
  const claims = creditVictories(debrief.victories);
  const summary = summariseClaims(claims);
  const downedAces = claims
    .filter((claim) => claim.confirmed)
    .map((claim) => FAMOUS_ACES.find((ace) => ace.name === claim.record.label)?.id)
    .filter((id): id is string => id !== undefined && !career.acesDowned.includes(id));
  const confirmed = addTallies(career.confirmed, summary.credited);
  return {
    career: {
      ...career,
      confirmed,
      unconfirmed: career.unconfirmed + summary.unconfirmed,
      squadronVictories: career.squadronVictories + debrief.squadronVictories,
      friendlyKills: career.friendlyKills + debrief.friendlyKills,
      careerScore: career.careerScore + debrief.missionScore,
      acesDowned: [...career.acesDowned, ...new Set(downedAces)],
    },
    claims,
    confirmed: summary.credited.aircraft + summary.credited.balloons + summary.credited.zeppelins,
    unconfirmed: summary.unconfirmed,
  };
}

function decorate(career: CareerState): { readonly career: CareerState; readonly awards: readonly MedalAward[] } {
  const awards = awardsEarned({
    nation: career.nation,
    victories: totalVictories(career),
    date: career.date,
    held: career.medals,
  });
  if (awards.length === 0) return { career, awards };
  const score = awards.reduce((sum, award) => sum + awardScore(award), 0);
  return {
    career: { ...career, medals: [...career.medals, ...awards], careerScore: career.careerScore + score },
    awards,
  };
}

function promote(career: CareerState): { readonly career: CareerState; readonly promotion: PromotionNotice | undefined } {
  const earned = rankForScore(career.careerScore);
  if (earned <= career.rank) return { career, promotion: undefined };
  const message = promotionMessage(career.nation, earned);
  const promotion = { rank: earned, title: message.replace("You are promoted to ", "").replace(/\.$/, ""), message };
  return { career: appendLog({ ...career, rank: earned }, message), promotion };
}

function discipline(
  career: CareerState,
  debrief: MissionDebrief,
): { readonly career: CareerState; readonly hearing: BoardHearing | undefined } {
  const hearing = boardHearing({
    friendlyKills: debrief.friendlyKills,
    strikes: career.strikes,
    service: SERVICE_LABELS[serviceFor(career.nation, career.date)],
  });
  if (!hearing) return { career, hearing: undefined };
  const withStrike: CareerState = {
    ...career,
    strikes: hearing.strike,
    status: hearing.grounded ? "discharged" : career.status,
  };
  return { career: appendLog(withStrike, `Board of Inquiry: ${hearing.finding}`), hearing };
}

function injure(
  career: CareerState,
  debrief: MissionDebrief,
  draw: MissionDraw,
): { readonly career: CareerState; readonly casualty: CasualtyReport | undefined } {
  const casualty = resolveCasualty({
    pilotKilled: debrief.pilotKilled,
    // A folded undercarriage writes the aeroplane off even though the pilot
    // taxied nowhere and walked away, so the squadron is an aeroplane down.
    aircraftLost: debrief.playerLost || debrief.gearCollapsed,
    wounded: debrief.playerWounded,
    overFriendlyTerritory: debrief.overFriendlyTerritory,
    landedSafely: debrief.landedSafely,
    // AD-021. Only a pilot who is alive on the ground under his canopy — a jump
    // that ended badly reaches `resolveCasualty` as `pilotKilled` and dies
    // there, before this is looked at.
    baledOut: debrief.baledOut && !debrief.pilotKilled,
    draw,
  });
  if (casualty.kind === "unhurt") return { career, casualty: undefined };
  const wounded: CareerState = { ...career, wounds: career.wounds + 1 };
  if (casualty.kind === "killed") {
    return { career: appendLog({ ...wounded, status: "dead" }, casualty.text), casualty };
  }
  return {
    career: appendLog({ ...wounded, status: casualty.kind === "captured" ? "pow" : "hospital" }, casualty.text),
    casualty,
  };
}

/**
 * What the squadron puts him in next. A pilot with a personal aircraft keeps
 * it; everyone else re-equips onto a type that has just been introduced, and
 * otherwise stays on his own as long as it is still front-line kit.
 */
function reEquip(
  career: CareerState,
  date: CareerDate,
  arrivals: readonly AircraftKey[],
  draw: MissionDraw,
): AircraftKey {
  if (career.personalAircraft) return career.aircraft;
  const newest = arrivals[arrivals.length - 1];
  if (newest) return newest;
  const issue = frontLineScouts(career.nation, date);
  return issue.includes(career.aircraft) ? career.aircraft : draw.pick(issue);
}

/** Moves the calendar on, re-equips the squadron, and notices the armistice. */
function advanceCalendar(
  career: CareerState,
  draw: MissionDraw,
): { readonly career: CareerState; readonly introductions: readonly AircraftIntroduction[] } {
  const flownOn = career.date;
  if (career.status === "dead") return { career, introductions: [] };
  const nextDate = addDays(flownOn, draw.int(MISSION_INTERVAL_DAYS[0], MISSION_INTERVAL_DAYS[1]));
  const arrivals = introducedBetween(career.nation, flownOn, nextDate);
  const introductions = arrivals.map((key) => ({
    aircraft: key,
    message: introductionMessage(career.nation, nextDate, key),
  }));
  const dated: CareerState = { ...career, date: nextDate, aircraft: reEquip(career, nextDate, arrivals, draw) };
  const armistice = isOnOrAfter(nextDate, WAR_END_DATE) && dated.status !== "discharged";
  return {
    career: armistice ? { ...dated, status: "retired", dutyReturnDate: undefined } : dated,
    introductions,
  };
}

function endingFor(career: CareerState): CareerEnding | undefined {
  if (career.status !== "dead" && career.status !== "discharged" && career.status !== "retired") return undefined;
  return { status: career.status, outcome: careerOutcome(career), epitaph: careerEpitaph(career) };
}

/**
 * Applies one sortie's debriefing. Throws on a career that is not on active
 * duty: a dead, grounded, retired, hospitalised or captured pilot cannot have
 * flown, so being handed his debriefing is a caller's bug, not a state to
 * absorb quietly.
 */
export function applyDebrief(career: CareerState, debrief: MissionDebrief): CareerUpdate {
  if (career.status !== "active") {
    throw new Error(`career ${career.id} is ${career.status} and cannot fly a mission`);
  }
  const flownOn = career.date;
  const draw = new MissionDraw(career.seed, `debrief:${career.missionsFlown + 1}`);
  const credited = creditClaims({ ...career, missionsFlown: career.missionsFlown + 1 }, debrief);
  const decorated = decorate(credited.career);
  const promoted = debrief.pilotKilled ? { career: decorated.career, promotion: undefined } : promote(decorated.career);
  const disciplined = discipline(promoted.career, debrief);
  const injured = injure(disciplined.career, debrief, draw);
  const advanced = advanceCalendar(injured.career, draw);
  const casualtyDays = injured.casualty?.lostDays ?? 0;
  const settled: CareerState =
    casualtyDays > 0 && advanced.career.status !== "dead" && advanced.career.status !== "retired"
      ? { ...advanced.career, dutyReturnDate: addDays(advanced.career.date, casualtyDays) }
      : advanced.career;
  return {
    career: settled,
    consequence: {
      date: flownOn,
      missionTitle: debrief.title,
      missionScore: debrief.missionScore,
      claims: credited.claims,
      confirmed: credited.confirmed,
      unconfirmed: credited.unconfirmed,
      awards: decorated.awards,
      citations: decorated.awards.map((award) => citationLines(award, { name: settled.name, nation: settled.nation })),
      promotion: promoted.promotion,
      hearing: disciplined.hearing,
      casualty: injured.casualty,
      introductions: advanced.introductions,
      nextDate: settled.date,
      ending: endingFor(settled),
    },
  };
}
